import { Buffer } from 'buffer';

export class BinaryWriter {
  static readonly kBytesPerInt16: number = 2;
  static readonly kBytesPerInt32: number = 4;
  static readonly kBytesPerFloat32: number = 4;
  static readonly kBytesPerFloat64: number = 8;

  static readonly kDefaultBufferSize: number = 256;

  private buffer: Buffer;
  private offset: number;

  constructor(initialBufferSize: number = BinaryWriter.kDefaultBufferSize) {
    this.buffer = Buffer.alloc(initialBufferSize);
    this.offset = 0;
  }

  toBytes(): Uint8Array {
    return Uint8Array.prototype.slice.call(this.buffer, 0, this.offset);
  }

  /**
   * @return {Uint8Array} Only the written portion of the buffer.
   */
  toMinimumBytes(): Uint8Array {
    return Buffer.from(this.buffer.subarray(0, this.offset));
  }

  writeBool(value: boolean): void {
    this.writeByte(value ? 1 : 0);
  }

  writeByte(value: number): void {
    this.reserveBytes(1);
    this.buffer.writeUInt8(value, this.offset++);
  }

  writeBytes(bytes: Uint8Array): void {
    this.reserveBytes(bytes.byteLength);
    for(let i = 0; i < bytes.byteLength; i++) {
      this.buffer.writeUInt8(bytes[i], this.offset++);
    }
  }

  writeUint8(value: number): void {
    this.reserveBytes(1);
    this.buffer.writeUInt8(value, this.offset++);
  }

  writeUInt16(value: number): void {
    this.reserveBytes(BinaryWriter.kBytesPerInt16);
    this.buffer.writeUInt16BE(value, this.offset);
    this.offset += BinaryWriter.kBytesPerInt16;
  }

  writeUInt32(value: number): void {
    this.reserveBytes(BinaryWriter.kBytesPerInt32);
    this.buffer.writeUInt32BE(value, this.offset);
    this.offset += BinaryWriter.kBytesPerInt32;
  }

  writeInt8(value: number): void {
    this.reserveBytes(1);
    this.buffer.writeInt8(value, this.offset++);
  }

  writeInt16(value: number): void {
    this.reserveBytes(BinaryWriter.kBytesPerInt16);
    this.buffer.writeInt16BE(value, this.offset);
    this.offset += BinaryWriter.kBytesPerInt16;
  }

  writeInt32(value: number): void {
    this.reserveBytes(BinaryWriter.kBytesPerInt32);
    this.buffer.writeInt32BE(value, this.offset);
    this.offset += BinaryWriter.kBytesPerInt32;
  }

  writeFloat32(value: number): void {
    this.reserveBytes(BinaryWriter.kBytesPerFloat32);
    this.buffer.writeFloatBE(value, this.offset);
    this.offset += BinaryWriter.kBytesPerFloat32;
  }

  writeFloat64(value: number): void {
    this.reserveBytes(BinaryWriter.kBytesPerFloat64);
    this.buffer.writeDoubleBE(value, this.offset);
    this.offset += BinaryWriter.kBytesPerFloat64;
  }

  private reserveBytes(count: number): void {
    if(this.buffer.byteLength < this.offset + count) {
      const size = BinaryWriter.powerOfTwo((this.offset + count) * 2);
      const buffer = Buffer.alloc(size);

      this.buffer.copy(buffer);
      this.buffer = buffer;
    }
  }

  //  https://stackoverflow.com/a/466242
  private static powerOfTwo(source: number): number {
    let x = source - 1;
    x |= x >> 1;
    x |= x >> 2;
    x |= x >> 4;
    x |= x >> 8;
    x |= x >> 16;
    x |= x >> 32;

    return x + 1;
  }
}
